import { useMemo } from 'react'
import { ServerApiClient } from '../../../data/ServerApiClient'
import { CfoCockpitPage } from './CfoCockpitPage'
import { resolveCfoPageFromNavId } from './cfoNav'
import { buildCfoRoutePath, cfoPageFromRoute, withFirmCockpitContext } from './cfoRoutes'
import { CfoFirmNavigationProvider, useCfoFirmNavigation } from './CfoFirmNavigation'
import { CfoPageShell, CfoShellSelectChip, type CfoShellSelectOption } from './CfoPageShell'

const siteSlugFromLocation = (): string => {
  if (typeof window === 'undefined') return ''
  const first = window.location.pathname.split('/').filter(Boolean)[0] || ''
  try {
    return decodeURIComponent(first)
  } catch {
    return first
  }
}

const navigateInApp = (target: string) => {
  if (typeof window === 'undefined' || !target || target === '/') return
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (current === target) return
  window.history.pushState(window.history.state, '', target)
  window.dispatchEvent(new Event('fg_route_change'))
}

function CfoFirmCockpitShell() {
  const navigation = useCfoFirmNavigation()
  const companyOptions = useMemo<CfoShellSelectOption[]>(
    () => navigation.clients.map((client) => ({
      id: client.workbook_id,
      label: client.company_name || client.client_name || 'CFO Core',
    })),
    [navigation.clients],
  )

  const openSelectedModule = (navId: string) => {
    const route = resolveCfoPageFromNavId(navId)
    const client = navigation.selectedClient
    if (!route || !client) return
    navigateInApp(withFirmCockpitContext(buildCfoRoutePath({
      siteSlug: client.site_slug || navigation.siteSlug,
      workbookId: client.workbook_id,
      page: cfoPageFromRoute(route),
    })))
  }

  const companySwitcher = companyOptions.length ? (
    <CfoShellSelectChip
      label="Select company"
      options={companyOptions}
      value={navigation.selectedWorkbookId}
      onChange={navigation.selectWorkbook}
      ariaLabel="Select company for CFO Core modules"
      menuAlign="end"
      className="cfo-firm-company-switcher"
    />
  ) : null

  return (
    <CfoPageShell
      activeNavId="firm"
      onNavSelect={openSelectedModule}
      brandLabel="CFO Core"
      brandSub={navigation.portfolio?.site_name || 'Firm cockpit'}
      crumb={<><b>Firm cockpit</b><span> · All companies</span></>}
      asOfLabel=""
      comparisonLabel=""
      showTopbar={false}
      dataSurface="firm_cockpit"
      navProfile="lightweight"
    >
      <CfoCockpitPage
        embedded
        headerActions={companySwitcher}
        selectedWorkbookId={navigation.selectedWorkbookId}
        onCompanySelect={(client) => navigation.selectWorkbook(client.workbook_id)}
      />
    </CfoPageShell>
  )
}

export function CfoFirmCockpitApp({ topInsetPx = 0 }: { topInsetPx?: number }) {
  const api = useMemo(() => new ServerApiClient(), [])
  const siteSlug = useMemo(() => siteSlugFromLocation(), [])
  return (
    <div style={{ height: '100vh', minHeight: 0, paddingTop: topInsetPx, boxSizing: 'border-box' }}>
      <CfoFirmNavigationProvider api={api} siteSlug={siteSlug} loadPortfolio>
        <CfoFirmCockpitShell />
      </CfoFirmNavigationProvider>
    </div>
  )
}
